import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import styles from '@components/forms/TaskCancelForm.module.css';
import useMyExpert from '@hooks/useMyExpert';
import { TaskCancelDocument, TaskCancelMutation, TaskCancelMutationVariables } from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { imageUrl } from '@utils/imageUtils';

type FormData = {
  note: string | null;
};

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
  taskId: string;
};

const TaskCancelForm = ({ onCancel, onSuccess, taskId }: Props) => {
  const initialValues = {
    note: ''
  };

  const myExpert = useMyExpert();
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [cancelTask] = useMutation<TaskCancelMutation, TaskCancelMutationVariables>(TaskCancelDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      note: myExpert
        ? yup.string().required(intl.formatMessage({ id: 'errors.taskCancel.note.blank' }))
        : yup.string().nullable().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return cancelTask({
      variables: {
        ...form.getValues(),
        canceled: true,
        id: taskId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.taskCancel,
        intl,
        onSuccess,
        setBaseErrors,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        setError: form.setError,
        txBase: 'errors.taskCancel'
      });
    });
  }, [cancelTask, form, taskId, intl, onSuccess]);

  return (
    <Form data-test="care-profile-notes-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <div className={styles.center}>
        <img
          alt={intl.formatMessage({ id: 'accessibility.altTags.envelopeHeart' })}
          className={styles.img}
          src={imageUrl('womanAtDesk')}
        />
      </div>
      <div className={styles.title}>
        <FormattedMessage id="forms.taskCancel.areYouSure" />
      </div>

      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        control={form.control}
        error={form.formState.errors.note}
        label={intl.formatMessage({ id: 'forms.taskCancel.noteLabel' })}
        name="note"
        required={!!myExpert}
      />

      <FormButtons
        cancelKey="forms.taskCancel.keepService"
        hookForm={form}
        onCancel={onCancel}
        saveKey="forms.taskCancel.cancelService"
      />
    </Form>
  );
};

export default TaskCancelForm;
